import React, { useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

const RETURNS_COL = "returns_v2";
const PAGE_SIZE = 500;

// ---- helpers ----
function getFormFromDoc(d) {
  if (!d) return {};
  if (d.type === "cyl") return d.cyl?.form || {};
  if (d.type === "parts") return d.parts?.form || {};
  return d.cyl?.form || d.parts?.form || {};
}

function getRN(d) {
  const f = getFormFromDoc(d);
  return String(f.returnsNoteNumber || "").trim();
}

function isDraftDoc(d) {
  // Your migrators stamp migratedFrom.collection like returns_draft / returns_parts_draft
  const src = d?.migratedFrom?.collection || "";
  if (typeof src === "string" && src.toLowerCase().includes("draft")) return true;

  // Fallback heuristic if migratedFrom not present:
  // - drafts often have status 'submitted' in old system, but that's not reliable.
  // So keep fallback minimal; main detection should be migratedFrom.collection.
  return false;
}

function tsMillis(ts) {
  // Firestore Timestamp OR string OR null
  if (!ts) return null;
  if (typeof ts?.toMillis === "function") return ts.toMillis();
  if (typeof ts?.toDate === "function") return ts.toDate().getTime();
  if (typeof ts === "string") {
    const ms = Date.parse(ts);
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

function docSortTime(d) {
  // prefer createdAt; then archivedAt; then submittedAt; then updatedAt
  return (
    tsMillis(d.createdAt) ??
    tsMillis(d.archivedAt) ??
    tsMillis(d.submittedAt) ??
    tsMillis(d.updatedAt) ??
    0
  );
}

function pickKeepAndDeletes(groupDocs) {
  // groupDocs: [{id, ...data}]
  const archived = groupDocs.filter((x) => x.archive === true);
  const active = groupDocs.filter((x) => x.archive !== true);

  // Rule 1: if ANY archived exists, keep ONE archived and delete everything else
  if (archived.length > 0) {
    // “remove newer duplicates” => keep OLDEST archived
    const keep = [...archived].sort((a, b) => docSortTime(a) - docSortTime(b))[0];
    const deletes = groupDocs.filter((x) => x.id !== keep.id);
    return { keep, deletes, reason: "Has archived -> keep archived (oldest), delete all others" };
  }

  // Rule 2: none archived
  const drafts = active.filter(isDraftDoc);
  const nonDrafts = active.filter((x) => !isDraftDoc(x));

  // If we have both, keep a non-draft (return version)
  if (nonDrafts.length > 0 && drafts.length > 0) {
    // keep OLDEST non-draft, delete other non-drafts + all drafts
    const keep = [...nonDrafts].sort((a, b) => docSortTime(a) - docSortTime(b))[0];
    const deletes = active.filter((x) => x.id !== keep.id);
    return { keep, deletes, reason: "Mixed draft+return -> keep return (oldest non-draft), delete others" };
  }

  // Otherwise: all drafts OR all non-drafts
  const pool = nonDrafts.length ? nonDrafts : drafts.length ? drafts : active;

  // keep OLDEST, delete newer
  const keep = [...pool].sort((a, b) => docSortTime(a) - docSortTime(b))[0];
  const deletes = active.filter((x) => x.id !== keep.id);
  return { keep, deletes, reason: "Same class -> keep oldest, delete newer duplicates" };
}

export default function ReturnsV2DedupeCleaner() {
  const [loading, setLoading] = useState(false);
  const [scannedCount, setScannedCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [err, setErr] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [log, setLog] = useState([]);

  const addLog = (line) => setLog((p) => [line, ...p].slice(0, 300));

  // 1) Load all docs (paged)
  const loadAll = async () => {
    setErr("");
    setLoading(true);
    setRows([]);
    setAnalysis(null);
    setLog([]);
    setScannedCount(0);

    try {
      const out = [];
      let last = null;
      let page = 0;

      while (true) {
        const qy = last
          ? query(
              collection(db, RETURNS_COL),
              orderBy("createdAt", "desc"),
              startAfter(last),
              limit(PAGE_SIZE)
            )
          : query(collection(db, RETURNS_COL), orderBy("createdAt", "desc"), limit(PAGE_SIZE));

        const snap = await getDocs(qy);
        if (snap.empty) break;

        page += 1;
        addLog(`Loaded page ${page} (${snap.size} docs)`);

        snap.docs.forEach((d) => out.push({ id: d.id, _snap: d, ...d.data() }));
        last = snap.docs[snap.docs.length - 1];
        if (snap.size < PAGE_SIZE) break;
      }

      setRows(out);
      setScannedCount(out.length);
      addLog(`DONE loading. Total docs loaded: ${out.length}`);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to load returns_v2");
    } finally {
      setLoading(false);
    }
  };

  // 2) Analyze duplicates by RN
  const analyze = () => {
    setErr("");
    setAnalysis(null);

    try {
      const byRN = new Map();

      for (const r of rows) {
        const rn = getRN(r);
        if (!rn) continue; // skip docs with no RN
        if (!byRN.has(rn)) byRN.set(rn, []);
        byRN.get(rn).push(r);
      }

      const dupes = [];
      let totalDeletes = 0;

      for (const [rn, group] of byRN.entries()) {
        if (group.length < 2) continue;

        const { keep, deletes, reason } = pickKeepAndDeletes(group);
        if (!keep || !deletes.length) continue;

        totalDeletes += deletes.length;

        dupes.push({
          rn,
          reason,
          keep: summarizeDoc(keep),
          deletes: deletes.map(summarizeDoc),
        });
      }

      // Sort: most deletions first
      dupes.sort((a, b) => b.deletes.length - a.deletes.length);

      setAnalysis({
        dupeGroups: dupes,
        dupeGroupCount: dupes.length,
        totalDeletes,
      });

      addLog(`Analysis complete: ${dupes.length} RN groups have duplicates; ${totalDeletes} docs marked for deletion.`);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Analysis failed");
    }
  };

  function summarizeDoc(d) {
    const f = getFormFromDoc(d);
    return {
      id: d.id,
      type: d.type,
      archive: d.archive === true,
      status: d.status || "",
      rn: f.returnsNoteNumber || "",
      serial: f.serialNumber || "",
      sret: f.sretNumber || "",
      createdAt: tsMillis(d.createdAt),
      archivedAt: tsMillis(d.archivedAt),
      submittedAt: tsMillis(d.submittedAt),
      sourceCol: d?.migratedFrom?.collection || "",
      isDraft: isDraftDoc(d),
    };
  }

  // 3) Delete duplicates (or just simulate)
  const runCleanup = async () => {
    if (!analysis?.dupeGroups?.length) {
      alert("No duplicates to clean (or you haven’t analyzed yet).");
      return;
    }

    const total = analysis.totalDeletes || 0;

    if (dryRun) {
      alert(`Dry-run is ON. Turn it off to actually delete.\n\nWould delete: ${total} docs.`);
      return;
    }

    if (
      !window.confirm(
        `This will DELETE ${total} docs from ${RETURNS_COL}.\n\nRules:\n- If any archived exists: keep archived only\n- Else: keep return over draft\n- Else: keep oldest\n\nProceed?`
      )
    ) {
      return;
    }

    setDeleting(true);
    setErr("");

    try {
      let done = 0;

      for (const grp of analysis.dupeGroups) {
        for (const del of grp.deletes) {
          await deleteDoc(doc(db, RETURNS_COL, del.id));
          done += 1;
          if (done % 25 === 0) addLog(`Deleted ${done}/${total}...`);
        }
      }

      addLog(`DONE. Deleted ${done} duplicate docs.`);
      alert(`Cleanup complete. Deleted ${done} docs.`);

      // reload fresh
      await loadAll();
      analyze();
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const previewGroups = useMemo(() => analysis?.dupeGroups || [], [analysis]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-bold text-gray-900">Returns V2 — Dedupe Cleaner (by RN)</h1>

        <button
          type="button"
          onClick={loadAll}
          disabled={loading || deleting}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          {loading ? "Loading…" : "Load returns_v2"}
        </button>

        <button
          type="button"
          onClick={analyze}
          disabled={loading || deleting || rows.length === 0}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Analyze duplicates
        </button>

        <label className="ml-auto flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
          />
          Dry-run (no deletes)
        </label>

        <button
          type="button"
          onClick={runCleanup}
          disabled={loading || deleting || !analysis}
          className={`px-4 py-2 rounded-md text-white ${
            dryRun ? "bg-gray-500 hover:bg-gray-600" : "bg-rose-600 hover:bg-rose-700"
          } disabled:opacity-50`}
        >
          {deleting ? "Deleting…" : dryRun ? "Simulate cleanup" : "DELETE duplicates"}
        </button>
      </div>

      {err ? <div className="text-sm text-red-600">{err}</div> : null}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6">
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 text-sm text-gray-700 flex flex-wrap gap-3">
            <div>Loaded: <b>{scannedCount}</b></div>
            <div>
              Duplicate groups: <b>{analysis?.dupeGroupCount ?? "—"}</b>
            </div>
            <div>
              Docs marked for deletion: <b className="text-rose-700">{analysis?.totalDeletes ?? "—"}</b>
            </div>
          </div>

          {!analysis ? (
            <div className="p-6 text-sm text-gray-600">
              Load returns_v2, then click <b>Analyze duplicates</b>.
            </div>
          ) : previewGroups.length === 0 ? (
            <div className="p-6 text-sm text-gray-600">No duplicates found by RN.</div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-left bg-white sticky top-0">
                  <tr className="border-b">
                    <th className="p-3">RN</th>
                    <th className="p-3">Keep</th>
                    <th className="p-3">Delete count</th>
                    <th className="p-3">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {previewGroups.map((g) => (
                    <tr key={g.rn} className="border-b align-top">
                      <td className="p-3 font-mono">{g.rn}</td>
                      <td className="p-3">
                        <div className="text-xs">
                          <div><b>{g.keep.type}</b> · {g.keep.archive ? "ARCHIVED" : "ACTIVE"} · {g.keep.isDraft ? "DRAFT" : "RETURN"}</div>
                          <div className="text-gray-500">id: {g.keep.id.slice(-8)} · status: {g.keep.status || "—"}</div>
                          <div className="text-gray-500">src: {g.keep.sourceCol || "—"}</div>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="inline-flex px-2 py-1 rounded border bg-rose-50 text-rose-700 border-rose-200 text-xs">
                          {g.deletes.length}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-gray-600">{g.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="p-4 text-xs text-gray-500">
                Note: This groups by RN only. Docs with missing RN are ignored.
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h2 className="text-lg font-semibold mb-2">Log</h2>
            {log.length === 0 ? (
              <div className="text-sm text-gray-600">Actions will appear here.</div>
            ) : (
              <div className="max-h-96 overflow-auto text-xs space-y-1">
                {log.map((l, i) => (
                  <div key={i} className="font-mono text-gray-700">{l}</div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h2 className="text-lg font-semibold mb-2">Safety notes</h2>
            <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
              <li>Dry-run is ON by default. Turn it off to actually delete.</li>
              <li>Deletes are permanent (no undo).</li>
              <li>Uses RN as the unique key. If RN can legitimately repeat, do not use this.</li>
              <li>Draft detection relies mainly on <code>migratedFrom.collection</code> containing “draft”.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
